
import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Zap, Shield, Heart, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatButton } from '@/components/chat/ChatButton';
import { Header } from '@/components/landing/Header';
import { Footer } from '@/components/landing/Footer';
import { BackgroundEffects } from '@/components/landing/BackgroundEffects';

export default function About() {
  const team = [
    {
      name: "Sarah Chen",
      role: "CEO & Co-Founder",
      bio: "Former VP of Engineering at TechCorp with 15+ years in AI and automation.",
      initials: "SC"
    },
    {
      name: "Marcus Rodriguez",
      role: "CTO & Co-Founder",
      bio: "Ex-Google AI researcher specializing in natural language processing and machine learning.",
      initials: "MR"
    },
    {
      name: "Emily Watson",
      role: "Head of Product",
      bio: "Product leader with expertise in B2B SaaS and user experience design.",
      initials: "EW"
    },
    {
      name: "David Kim",
      role: "Head of Engineering",
      bio: "Full-stack architect with experience scaling platforms to millions of users.",
      initials: "DK"
    }
  ];

  const values = [
    {
      icon: Users,
      title: "Customer First",
      description: "Every decision we make starts with our customers' success in mind."
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "We're constantly pushing the boundaries of what's possible with AI."
    },
    {
      icon: Shield,
      title: "Trust & Security",
      description: "Your data security and privacy are our highest priorities."
    },
    {
      icon: Heart,
      title: "Simplicity",
      description: "Complex problems deserve simple, elegant solutions."
    }
  ];

  const stats = [
    { value: "2019", label: "Founded" },
    { value: "50K+", label: "Active Users" },
    { value: "99.9%", label: "Uptime" },
    { value: "40+", label: "Team Members" }
  ];

  return (
    <div className="bg-neutral-900 text-white relative overflow-hidden min-h-screen">
      <BackgroundEffects />
      <Header />
      
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Building the future of{' '}
            <span className="text-yellow-400 relative">
              AI-powered work
              <div className="absolute -bottom-2 left-0 w-full h-3 bg-yellow-400/20 rounded-full"></div>
            </span>
          </h1>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            We're on a mission to democratize artificial intelligence and make advanced automation accessible to every business.
          </p>
        </div>
      </div>

      {/* Company Story */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Our <span className="text-yellow-400">story</span>
              </h2>
              <div className="space-y-6 text-neutral-300 leading-relaxed">
                <p>
                  Founded in 2019, atWork.ai was born from a simple observation: most businesses were struggling to keep up with the rapid pace of digital transformation while managing increasingly complex workflows.
                </p>
                <p>
                  Our founders, having experienced these challenges firsthand at scale, envisioned a world where artificial intelligence could seamlessly integrate into everyday business processes, making teams more productive and freeing them to focus on what truly matters.
                </p>
                <p>
                  Today, we're proud to serve thousands of businesses worldwide, helping them automate their sales processes, generate qualified leads, and build stronger customer relationships through the power of AI.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-yellow-400/20 to-neutral-800/50 rounded-2xl p-8 border border-neutral-700/50">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl font-bold text-yellow-400 mb-2">{stat.value}</div>
                      <div className="text-neutral-300 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-20 px-4 bg-neutral-800/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Our <span className="text-yellow-400">values</span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 bg-neutral-800/50 border border-neutral-700/50 rounded-2xl">
                <div className="inline-flex justify-center items-center w-16 h-16 bg-yellow-400/10 border border-yellow-400/20 rounded-2xl text-yellow-400 mb-6">
                  <value.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-white">{value.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Meet our <span className="text-yellow-400">team</span>
            </h2>
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              The brilliant minds behind atWork.ai
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center p-6 bg-neutral-800/50 border border-neutral-700/50 rounded-2xl hover:bg-neutral-800/70 transition-colors">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-neutral-900 font-bold text-xl mx-auto mb-6">
                  {member.initials}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{member.name}</h3>
                <p className="text-yellow-400 font-medium mb-4">{member.role}</p>
                <p className="text-neutral-400 text-sm leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20 px-4 bg-neutral-800/30">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 rounded-3xl p-12 md:p-16">
            <Award className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our mission
            </h2>
            <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
              To empower every business with intelligent automation that enhances human potential rather than replacing it. We believe AI should amplify creativity, eliminate repetitive tasks, and enable teams to focus on strategic, high-impact work.
            </p>
            <Button asChild size="lg" className="bg-yellow-400 text-neutral-900 hover:bg-yellow-300 px-8 py-4 rounded-full font-semibold">
              <Link to="/contact">Join Our Mission</Link>
            </Button>
          </div>
        </div>
      </div>

      <Footer />
      <ChatButton />
    </div>
  );
}
